import { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { TextContext } from "../context/TextContext";
import { getStudentsOfClassroom, deleteStudent } from "../api/studentsService";
import AddStudentComponent from "./AddStudentComponent";
import UpdateStudentComponent from "./UpdateStudentComponent";
import { insertAbsences } from "../api/absenceService";

const StudentsOfClassroom = () => {
    const navigate = useNavigate();

    const {
        setSharedText,
        selectedDate,
        selectedClassroomId
    } = useContext(TextContext);

    const [students, setStudents] = useState([]);
    const [isAbsenceChange, setIsAbsenceChange] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [editingStudent, setEditingStudent] = useState(null);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const searchInputRef = useRef(null);

    useEffect(() => {
        if (isSearchOpen) {
            searchInputRef.current?.focus();
        }
    }, [isSearchOpen]);

    useEffect(() => {
        setSharedText("لیست دانش‌آموزان");
        getStudents();
    }, [selectedClassroomId, selectedDate]);

    const getStudents = async () => {
        try {
            setIsLoading(true);

            const data = await getStudentsOfClassroom(
                selectedClassroomId,
                selectedDate
            );

            setStudents(data.students || []);
            setIsAbsenceChange(false);

        } catch (err) {
            toast.error(
                err.response?.data?.message ||
                "دریافت لیست دانش‌آموزان با خطا مواجه شد."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const studentClicked = (studentId) => {
        setStudents((current) => {
            return current.map((student) =>
                student.id === studentId
                    ? { ...student, isAbsence: !student.isAbsence }
                    : student
            );
        });

        setIsAbsenceChange(true);
    };

    const afterAddStudent = (id, name, family) => {
        setStudents((current) => [
            ...current,
            {
                id,
                name,
                family,
                isAbsence: false
            }
        ]);
    };

    const afterUpdateStudent = (updatedStudent) => {
        setStudents((current) =>
            current.map((student) =>
                student.id === updatedStudent.id ? updatedStudent : student
            )
        );
    };

    const deleteStudentHandler = async (student) => {
        const confirmed = window.confirm(
            `آیا از حذف «${student.name} ${student.family}» مطمئن هستید؟`
        );

        if (!confirmed) return;

        try {
            setDeletingId(student.id);

            await deleteStudent(student.id);

            setStudents((current) =>
                current.filter(
                    (item) => item.id !== student.id
                )
            );

            toast.success("دانش‌آموز با موفقیت حذف شد.");

        } catch (err) {
            toast.error(
                err.response?.data?.message ||
                "حذف دانش‌آموز انجام نشد."
            );
        } finally {
            setDeletingId(null);
        }
    };

    const addAbsences = async () => {
        if (!isAbsenceChange || isSaving) return;

        try {
            setIsSaving(true);

            const data = students.map((item) => ({
                studentId: item.id,
                isAbsence: item.isAbsence
            }));

            await insertAbsences(
                selectedClassroomId,
                selectedDate,
                data
            );

            setIsAbsenceChange(false);

            navigate(-1);

        } catch (err) {
            toast.error(
                err.response?.data?.message ||
                "ثبت حضور و غیاب انجام نشد."
            );
        } finally {
            setIsSaving(false);
        }
    };

    const absentCount = students.filter(
        (student) => student.isAbsence
    ).length;

    const presentCount = students.length - absentCount;
    const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase();
    const filteredStudents = normalizedSearchQuery
        ? students.filter((student) =>
            [student.name, student.family].some((value) =>
                (value || "")
                    .toLocaleLowerCase()
                    .includes(normalizedSearchQuery)
            )
        )
        : students;
    const filteredAbsentCount = filteredStudents.filter(
        (student) => student.isAbsence
    ).length;

    return (
        <div className="students-page" dir="rtl">

            {/* ================= HEADER ================= */}

            <header className="students-header">

                <div className="students-header-inner">

                    <div className={`header-info ${isSearchOpen ? "search-open" : ""}`}>
                        <div className="header-icon">
                            <svg
                                width="21"
                                height="21"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M16 21V19C16 16.79 14.21 15 12 15H6C3.79 15 2 16.79 2 19V21"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                    strokeLinecap="round"
                                />
                                <circle
                                    cx="9"
                                    cy="7"
                                    r="4"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                />
                                <path
                                    d="M19 8V14M22 11H16"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>

                        <div className="header-text">
                            <h1>لیست دانش‌آموزان</h1>
                        </div>

                    </div>

                    <button
                        type="button"
                        className="search-students-button"
                        aria-label={isSearchOpen ? "بستن جستجو" : "جستجوی دانش‌آموز"}
                        aria-expanded={isSearchOpen}
                        onClick={() => {
                            setIsSearchOpen((isOpen) => !isOpen);
                            if (isSearchOpen) setSearchQuery("");
                        }}
                    >
                        <svg
                            width="19"
                            height="19"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <circle
                                cx="11"
                                cy="11"
                                r="7"
                                stroke="currentColor"
                                strokeWidth="1.8"
                            />
                            <path
                                d="M16 16L21 21"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>

                    {isSearchOpen && (
                        <input
                            ref={searchInputRef}
                            type="search"
                            className="form-control header-search-input"
                            dir="rtl"
                            aria-label="جستجو بر اساس نام یا نام خانوادگی"
                            placeholder="نام یا نام خانوادگی..."
                            value={searchQuery}
                            onChange={(event) =>
                                setSearchQuery(event.target.value)
                            }
                        />
                    )}

                    <button
                        type="button"
                        className="add-student-button"
                        data-bs-toggle="modal"
                        data-bs-target="#addStudentModal"
                    >
                        <svg
                            width="19"
                            height="19"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                d="M12 5V19M5 12H19"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>

                        <span>دانش‌آموز</span>
                    </button>

                </div>

            </header>


            {/* ================= CONTENT ================= */}

            <main className="students-content">

                {!isLoading && filteredStudents.length > 0 && (

                    <>

                        <div className="list-heading">

                            <div>
                                <strong>دانش‌آموزان</strong>

                                <span>
                                    {filteredStudents.length} نفر
                                </span>
                            </div>

                            {filteredAbsentCount > 0 && (
                                <span className="absence-total">
                                    {filteredAbsentCount} غایب
                                </span>
                            )}

                        </div>


                        {/* Students */}

                        <div className="students-list">

                            {filteredStudents.map((student) => (

                                <div
                                    key={student.id}
                                    className={
                                        "student-card " +
                                        (student.isAbsence
                                            ? "student-absent"
                                            : "")
                                    }
                                    onClick={() =>
                                        studentClicked(student.id)
                                    }
                                >

                                    <div className="student-content">

                                        {/* Status */}

                                        <div
                                            className={
                                                "status-indicator " +
                                                (student.isAbsence
                                                    ? "status-absent"
                                                    : "status-present")
                                            }
                                        >
                                            {student.isAbsence ? (
                                                <svg
                                                    width="19"
                                                    height="19"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M7 7L17 17M17 7L7 17"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            ) : (
                                                <svg
                                                    width="19"
                                                    height="19"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M5 12.5L9.5 17L19 7"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            )}
                                        </div>


                                        {/* Avatar */}



                                        {/* Info */}

                                        <div className="student-info">

                                            <div className="student-name">
                                                {student.name}{" "}
                                                {student.family}
                                            </div>

                                            <div className="student-status">

                                                {student.isAbsence ? (
                                                    <>
                                                        <span className="status-dot absent-dot" />
                                                        غایب
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="status-dot present-dot" />
                                                        حاضر
                                                    </>
                                                )}

                                            </div>

                                        </div>


                                        {/* Menu */}

                                        <div
                                            className="student-menu dropdown"
                                            onClick={(e) =>
                                                e.stopPropagation()
                                            }
                                        >

                                            <button
                                                type="button"
                                                className="more-button"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                                aria-label="عملیات دانش‌آموز"
                                            >
                                                <svg
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <circle
                                                        cx="12"
                                                        cy="5"
                                                        r="1.4"
                                                        fill="currentColor"
                                                    />
                                                    <circle
                                                        cx="12"
                                                        cy="12"
                                                        r="1.4"
                                                        fill="currentColor"
                                                    />
                                                    <circle
                                                        cx="12"
                                                        cy="19"
                                                        r="1.4"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                            </button>

                                            <ul className="dropdown-menu dropdown-menu-end shadow-sm">

                                                <li>
                                                    <button
                                                        type="button"
                                                        className="dropdown-item text-end"
                                                        onClick={() => navigate("/student-absences", { state: { studentId: student.id } })}>
                                                       لیست غیبت ها 
                                                    </button>
                                                </li>
                                                <li>
                                                    <button
                                                        type="button"
                                                        className="dropdown-item text-end"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#updateStudentModal"
                                                        onClick={() => setEditingStudent(student)}>
                                                        ویرایش دانش‌آموز
                                                    </button>
                                                </li>
                                                <li>
                                                    <button
                                                        type="button"
                                                        className="dropdown-item text-end"
                                                        disabled={
                                                            deletingId ===
                                                            student.id
                                                        }
                                                        onClick={() =>
                                                            deleteStudentHandler(
                                                                student
                                                            )
                                                        }
                                                    >
                                                        {deletingId ===
                                                            student.id
                                                            ? "در حال حذف..."
                                                            : "حذف دانش‌آموز"}
                                                    </button>
                                                </li>

                                            </ul>

                                        </div>

                                    </div>

                                </div>

                            ))}

                        </div>

                    </>
                )}


                {/* Empty */}

                {!isLoading && students.length > 0 && filteredStudents.length === 0 && (
                    <div className="empty-state">
                        <h2>دانش‌آموزی پیدا نشد</h2>
                        <p>
                            برای «{searchQuery.trim()}» نتیجه‌ای در فهرست دانش‌آموزان وجود ندارد.
                        </p>
                    </div>
                )}

                {!isLoading && students.length === 0 && (

                    <div className="empty-state">

                        <div className="empty-icon">
                            <svg
                                width="34"
                                height="34"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M16 21V19C16 16.79 14.21 15 12 15H6C3.79 15 2 16.79 2 19V21"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                    strokeLinecap="round"
                                />

                                <circle
                                    cx="9"
                                    cy="7"
                                    r="4"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                />

                                <path
                                    d="M19 8V14M22 11H16"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>

                        <h2>هنوز دانش‌آموزی ثبت نشده است</h2>

                        <p>
                            برای شروع، یک دانش‌آموز به این کلاس اضافه کنید.
                        </p>

                        <button
                            type="button"
                            className="empty-add-button"
                            data-bs-toggle="modal"
                            data-bs-target="#addStudentModal"
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M12 5V19M5 12H19"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>

                            افزودن دانش‌آموز
                        </button>

                    </div>
                )}


                {/* Loading */}

                {isLoading && (

                    <div className="loading-state">

                        <div className="loading-icon">
                            <div className="spinner-border text-primary" />
                        </div>

                        <strong>
                            در حال دریافت دانش‌آموزان
                        </strong>

                        <span>
                            لطفاً چند لحظه صبر کنید...
                        </span>

                    </div>
                )}

            </main>


            {/* ================= SAVE BAR ================= */}

            {!isLoading && isAbsenceChange && (

                <div className="save-bar">

                    <div className="save-bar-inner">

                        <div className="save-info">

                            {/* <div> */}
                            <div className="summary-stats">

                                <div className="stat present">
                                    <strong className="ms-2">{presentCount}</strong>
                                    <span>حاضر</span>
                                </div>

                                <div className="stat-divider" />

                                <div className="stat absent">
                                    <strong className="ms-2">{absentCount}</strong>
                                    <span>غایب</span>
                                </div>

                            {/* </div> */}
                            </div>
                        </div>

                        <button
                            type="button"
                            className="save-button"
                            disabled={isSaving}
                            onClick={addAbsences}
                        >
                            {isSaving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" />
                                    در حال ثبت
                                </>
                            ) : (
                                <>
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M5 12.5L9.5 17L19 7"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>

                                    ثبت تغییرات
                                </>
                            )}
                        </button>

                    </div>

                </div>
            )}


            <AddStudentComponent
                classroomId={selectedClassroomId}
                afterAddStudent={afterAddStudent}
            />
            <UpdateStudentComponent
                student={editingStudent}
                afterUpdateStudent={afterUpdateStudent}
            />


            {/* ================= STYLE ================= */}

            <style>{`

                * {
                    box-sizing: border-box;
                }

                .students-page {
                    min-height: 100vh;
                    background: #f6f8fc;
                    color: #172033;
                    font-family: inherit;
                    padding-bottom: 50px;
                }

                /* HEADER */

                .students-header {
                    height: 72px;

                    position: sticky;
                    top: 0;
                    z-index: 100;

                    background: #726be7;
                    color: #fff;
                    border-bottom: 1px solid #e8edf4;

                    backdrop-filter: blur(12px);
                }

                .students-header-inner {
                    max-width: 700px;
                    height: 100%;

                    margin: auto;
                    padding: 0 15px;

                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    gap: 12px;
                }

                .header-info {
                    min-width: 0;
                    flex: 1;

                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .search-students-button {
                    width: 40px;
                    height: 40px;

                    padding: 0;
                    flex-shrink: 0;

                    border: 1px solid rgba(255,255,255,.55);
                    border-radius: 11px;

                    background: transparent;
                    color: #fff;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .search-students-button:hover {
                    background: rgba(255,255,255,.12);
                }

                .header-search-input {
                    flex: 1 1 160px;
                    min-width: 0;
                    max-width: 240px;
                    height: 40px;
                    border: 0;
                    border-radius: 11px;
                }

                .back-button,
                .header-icon {
                    width: 42px;
                    height: 42px;

                    flex-shrink: 0;

                    border-radius: 12px;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .back-button {
                    border: 1px solid #e4e9f0;
                    background: #fff;
                    color: #687589;
                }

                .back-button svg {
                    transform: rotate(180deg);
                }

                .header-icon {
                    background: #eaf2ff;
                    color: #2563eb;
                }

                .header-text {
                    min-width: 0;
                }

                .header-text h1 {
                    margin: 0;

                    font-size: 15px;
                    line-height: 1.6;

                    font-weight: 800;

                    white-space: nowrap;
                }

                .header-text span {
                    display: block;

                    margin-top: 1px;

                    font-size: 10px;
                    line-height: 1.6;

                    color: #7d8798;

                    white-space: nowrap;
                }

                .add-student-button {
                    height: 40px;

                    padding: 0 12px;

                    border: 0;
                    border-radius: 11px;

                    background: #2563eb;
                    color: #fff;

                    display: flex;
                    align-items: center;
                    justify-content: center;

                    gap: 6px;

                    font-size: 11px;
                    font-weight: 700;

                    white-space: nowrap;

                    box-shadow: 0 4px 12px rgba(37,99,235,.16);

                    transition: .18s ease;
                }

                .add-student-button:hover {
                    background: #1d4ed8;
                }

                /* CONTENT */

                .students-content {
                    width: 100%;
                    max-width: 700px;

                    margin: auto;

                    padding: 20px 15px;
                }

                /* SUMMARY */

.summary-card{position:sticky;display:flex;justify-content:space-around;top:70px;z-index:10;background:#fff;border:1px solid #edf0f5;border-radius:18px;box-shadow:0 8px 25px rgba(0,0,0,.06);}

                .summary-title strong {
                    display: block;

                    font-size: 13px;
                    line-height: 1.7;

                    font-weight: 800;
                }

                .summary-title span {
                    display: block;

                    margin-top: 3px;

                    font-size: 10px;
                    line-height: 1.7;

                    color: #7d8798;
                }

                .summary-stats {
                    display: flex;
                    align-items: center;

                    gap: 13px;
                }

                .stat {
                    min-width: 42px;

                    display: flex;
                    align-items: center;
                }

                .stat strong {
                    font-size: 17px;
                    line-height: 1.3;
                    font-weight: 800;
                }

                .stat span {
                    margin-top: 2px;

                    font-size: 9px;
                    color: #7d8798;
                }

                .stat.present strong {
                    color: #15945c;
                }

                .stat.absent strong {
                    color: #d81b45;
                }

                .stat-divider {
                    width: 1px;
                    height: 30px;
                    background: #e8edf3;
                }

                /* LIST HEADING */

                .list-heading {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    padding: 5px 3px 8px;
                }

                .list-heading > div {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .list-heading strong {
                    font-size: 14px;
                    font-weight: 800;
                }

                .list-heading span {
                    font-size: 10px;
                    color: #8a94a5;
                }

                .absence-total {
                    padding: 4px 8px;

                    border-radius: 7px;

                    background: #fff0f2;
                    color: #d81b45 !important;

                    font-size: 9px !important;
                    font-weight: 700;
                }

                /* STUDENT LIST */

                .students-list {
                    display: flex;
                    flex-direction: column;
                    gap: 9px;
                }

                .student-card {
                    padding: 13px;

                    border: 1px solid #e7ebf2;
                    border-radius: 16px;

                    background: #fff;

                    cursor: pointer;

                    box-shadow:
                        0 2px 9px rgba(25,45,75,.025);

                    transition:
                        transform .16s ease,
                        border-color .16s ease,
                        background .16s ease,
                        box-shadow .16s ease;

                    -webkit-tap-highlight-color: transparent;
                }

                .student-card:active {
                    transform: scale(.985);
                }

                .student-content {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                }

                .student-absent {
                    background: #f2e4e6;
                    border-color: #f4dce1;
                }

                /* STATUS */

                .status-indicator {
                    width: 34px;
                    height: 34px;

                    flex-shrink: 0;

                    border-radius: 10px;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .status-present {
                    background: #eaf8f1;
                    color: #15945c;
                }

                .status-absent {
                    background: #fff0f2;
                    color: #d81b45;
                }

                /* AVATAR */

                .student-avatar {
                    width: 43px;
                    height: 43px;

                    flex-shrink: 0;

                    border-radius: 13px;

                    background: #edf4ff;
                    color: #2563eb;

                    display: flex;
                    align-items: center;
                    justify-content: center;

                    font-size: 15px;
                    font-weight: 800;
                }

                .avatar-absent {
                    background: #fff0f2;
                    color: #d81b45;
                }

                /* INFO */

                .student-info {
                    flex: 1;
                    min-width: 0;
                }

                .student-name {
                    font-size: 14px;
                    line-height: 1.7;

                    font-weight: 800;

                    color: #202b3d;

                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .student-status {
                    margin-top: 2px;

                    display: flex;
                    align-items: center;
                    gap: 5px;

                    font-size: 9px;
                    line-height: 1.6;

                    color: #7d8798;
                }

                .status-dot {
                    width: 6px;
                    height: 6px;

                    border-radius: 50%;
                }

                .present-dot {
                    background: #20a86b;
                }

                .absent-dot {
                    background: #e11d48;
                }

                /* MENU */

                .student-menu {
                    flex-shrink: 0;
                }

                .more-button {
                    width: 38px;
                    height: 38px;

                    padding: 0;

                    border: 0;
                    border-radius: 10px;

                    background: transparent;
                    color: #8a94a5;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .more-button:hover {
                    background: #f3f5f8;
                }

                /* EMPTY */

                .empty-state {
                    min-height: 55vh;

                    display: flex;
                    flex-direction: column;

                    align-items: center;
                    justify-content: center;

                    text-align: center;
                }

                .empty-icon {
                    width: 76px;
                    height: 76px;

                    border-radius: 22px;

                    background: #edf4ff;
                    color: #2563eb;

                    display: flex;
                    align-items: center;
                    justify-content: center;

                    margin-bottom: 17px;
                }

                .empty-state h2 {
                    margin: 0;

                    font-size: 16px;
                    font-weight: 800;
                }

                .empty-state p {
                    max-width: 290px;

                    margin: 7px 0 17px;

                    color: #7d8798;

                    font-size: 11px;
                    line-height: 1.9;
                }

                .empty-add-button {
                    height: 42px;

                    padding: 0 15px;

                    border: 0;
                    border-radius: 11px;

                    background: #2563eb;
                    color: #fff;

                    display: flex;
                    align-items: center;
                    justify-content: center;

                    gap: 7px;

                    font-size: 11px;
                    font-weight: 700;
                }

                /* LOADING */

                .loading-state {
                    min-height: 55vh;

                    display: flex;
                    flex-direction: column;

                    align-items: center;
                    justify-content: center;

                    text-align: center;
                }

                .loading-icon {
                    width: 56px;
                    height: 56px;

                    margin-bottom: 15px;

                    border-radius: 17px;

                    background: #edf4ff;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .loading-icon .spinner-border {
                    width: 25px;
                    height: 25px;
                }

                .loading-state strong {
                    font-size: 13px;
                }

                .loading-state span {
                    margin-top: 5px;

                    font-size: 10px;
                    color: #8a94a5;
                }

                /* SAVE BAR */

                .save-bar {
                    position: fixed;

                    left: 0;
                    right: 0;
                    bottom: 0;

                    z-index: 200;

                    padding: 10px 15px;

                    background: rgba(255,255,255,.94);
                    border-top: 1px solid #e5eaf1;

                    backdrop-filter: blur(14px);

                    box-shadow:
                        0 -6px 25px rgba(20,35,60,.07);
                }

                .save-bar-inner {
                    max-width: 700px;

                    margin: auto;

                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    gap: 12px;
                }

                .save-info {
                    min-width: 0;

                    display: flex;
                    align-items: center;
                    gap: 9px;
                }

                .save-icon {
                    width: 36px;
                    height: 36px;

                    flex-shrink: 0;

                    border-radius: 10px;

                    background: #fff6e6;
                    color: #d88a00;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .save-info strong {
                    display: block;

                    font-size: 15px;
                    line-height: 1.6;

                    font-weight: 800;
                }

                .save-info span {
                    display: block;

                    margin-top: 1px;

                    font-size: 14px;
                    line-height: 1.6;

                    color: #7d8798;
                }

                .save-button {
                    height: 42px;

                    padding: 0 16px;

                    flex-shrink: 0;

                    border: 0;
                    border-radius: 11px;

                    background: #2563eb;
                    color: #fff;

                    display: flex;
                    align-items: center;
                    justify-content: center;

                    gap: 7px;

                    font-size: 14px;
                    font-weight: 700;

                    box-shadow:
                        0 4px 12px rgba(37,99,235,.18);
                }

                .save-button:disabled {
                    opacity: .7;
                    cursor: not-allowed;
                }

                /* MOBILE */

                @media (max-width: 480px) {

                    .students-header {
                        height: 68px;
                    }

                    .students-header-inner {
                        padding: 0 10px;
                    }

                    .header-info.search-open {
                        display: none;
                    }

                    .back-button,
                    .header-icon {
                        width: 39px;
                        height: 39px;
                    }

                    .header-text h1 {
                        font-size: 14px;
                    }

                    .header-text span {
                        font-size: 9px;
                    }

                    .add-student-button {
                        width: 40px;
                        height: 40px;

                        padding: 0;

                        border-radius: 11px;
                    }

                    .header-search-input {
                        flex-basis: 0;
                        max-width: none;
                    }

                    .add-student-button span {
                        display: none;
                    }

                    .students-content {
                        padding: 16px 10px 25px;
                    }

                    .summary-card {
                        padding: 13px;
                    }

                    .summary-title span {
                        font-size: 9px;
                    }

                    .student-card {
                        padding: 12px;
                    }

                    .status-indicator {
                        width: 32px;
                        height: 32px;
                    }

                    .student-avatar {
                        width: 41px;
                        height: 41px;
                    }

                    .student-name {
                        font-size: 13px;
                    }

                    .save-bar {
                        padding: 9px 10px;
                    }

                    .save-button {
                        height: 40px;
                        padding: 0 13px;
                    }

                }

                @media (min-width: 768px) {

                    .student-card:hover {
                        transform: translateY(-1px);

                        border-color: #d8e2f2;

                        box-shadow:
                            0 8px 22px rgba(25,45,75,.07);
                    }

                    .student-absent:hover {
                        border-color: #eccfd5;
                    }

                }

            `}</style>

        </div>
    );
};

export default StudentsOfClassroom;